import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import HrTermStateModal, { HrRelatedTermFormValues } from 'components/modals/hr-term-state/HrTermStateModal';
import panic from 'errors/panic';
import { useEffect } from 'react';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

interface HrReactivateTermModalProps {
  term: any | null;
  onClose: () => void;
  onReactivated: (term: unknown) => void | Promise<void>;
  onConflict: () => void;
}

/** Reusable standalone term-reactivation workflow for HR term tables and details. */
export default function HrReactivateTermModal({
  term,
  onClose,
  onReactivated,
  onConflict,
}: HrReactivateTermModalProps) {
  const { getAction } = useApi();
  const form = useForm<HrRelatedTermFormValues>({
    initialValues: {
      termTypeId: '',
      nextDueDate: '',
      recurrenceQuantity: 1,
      recurrenceUnit: 'years',
      note: '',
      checkedVariantIds: [],
      certificateId: '',
      certificateNumber: '',
      renewalDate: '',
      certificateFileId: '',
      stampFileId: '',
    },
  });

  useEffect(() => {
    form.setFieldValue('nextDueDate', term?.nextDueDate ?? '');
    form.clearFieldError('nextDueDate');
  }, [term]);

  const submit = form.onSubmit(async (values) => {
    if (!term) return;
    if (!values.nextDueDate) {
      form.setFieldError('nextDueDate', 'Vyberte najbližší termín');
      return;
    }

    try {
      const reactivated = await getAction('HrSetTermStatus')({
        parameters: { termId: String(term.termId) },
        payload: { version: term.version, status: 'active', nextDueDate: values.nextDueDate },
      });
      notifications.show({
        title: 'HR termín bol zaradený',
        message: 'Termín je opäť aktívny.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      onClose();
      await onReactivated(reactivated);
    } catch (caught: any) {
      if (caught?.response?.status === 409) {
        onConflict();
      } else {
        panic(caught as Error);
      }
    }
  });

  return (
    <HrTermStateModal
      mode={term ? 'reactivate' : null}
      form={form}
      certificateOptions={[]}
      certificateOptionsLoading={false}
      onClose={onClose}
      onSubmit={submit}
    />
  );
}
